import type { UpdateJobInput } from '@chukua/common';
import type { TabsProps } from 'antd';
import type { ContainerSize, ContainerType, JobType, SpecialHandlingRequirement } from '../../reference-data/reference-data.api';
import { JobAttachments } from '../features/uploads/components/JobAttachments';
import { JobCargoTab } from '../features/cargo/components/JobCargoTab';
import { JobCommercialTermsTab } from '../features/commercial-terms/components/JobCommercialTermsTab';
import { JobDetailsTab } from './JobDetailsTab';
import { JobHistoryTab } from './JobHistoryTab';
import { JOB_TAB_LABELS } from '../jobs.paths';
import { JobReadinessTab } from '../features/readiness/components/JobReadinessTab';
import { JobTripsTab } from '../features/trips/components/JobTripsTab';
import type { JobDetail } from '../jobs.api';
import type { JobCataloguesState } from '../hooks/use-job-catalogues';

interface BuildJobDetailTabsArgs {
  job: JobDetail;
  catalogues: JobCataloguesState;
  jobTypes: JobType[];
  containerSizes: ContainerSize[];
  containerTypes: ContainerType[];
  specialHandlingRequirements: SpecialHandlingRequirement[];
  updateError: string | null;
  saving: boolean;
  canManage: boolean;
  onUpdate: (input: UpdateJobInput) => Promise<void>;
  onAttachmentUploaded: () => void;
  onChanged: () => Promise<void>;
}

/** The record page's seven tabs — its own function so `JobDetailPage` stays a thin state-to-props wire-up. */
export function buildJobDetailTabs({
  job,
  catalogues,
  jobTypes,
  containerSizes,
  containerTypes,
  specialHandlingRequirements,
  updateError,
  saving,
  canManage,
  onUpdate,
  onAttachmentUploaded,
  onChanged,
}: BuildJobDetailTabsArgs): TabsProps['items'] {
  return [
    {
      key: 'details',
      label: JOB_TAB_LABELS.details,
      children: <JobDetailsTab job={job} jobTypes={jobTypes} error={updateError} saving={saving} onSave={onUpdate} />,
    },
    {
      key: 'cargo',
      label: JOB_TAB_LABELS.cargo,
      children: (
        <JobCargoTab
          job={job}
          catalogues={catalogues}
          jobTypes={jobTypes}
          containerSizes={containerSizes}
          containerTypes={containerTypes}
          specialHandlingRequirements={specialHandlingRequirements}
          onChanged={onChanged}
        />
      ),
    },
    {
      key: 'terms',
      label: JOB_TAB_LABELS.terms,
      children: <JobCommercialTermsTab job={job} canManage={canManage} error={updateError} saving={saving} onSave={onUpdate} />,
    },
    {
      key: 'attachments',
      label: JOB_TAB_LABELS.attachments,
      children: <JobAttachments jobId={job.id} attachments={job.attachments} onUploaded={onAttachmentUploaded} />,
    },
    { key: 'readiness', label: JOB_TAB_LABELS.readiness, children: <JobReadinessTab job={job} canManage={canManage} onChanged={onChanged} /> },
    { key: 'trips', label: JOB_TAB_LABELS.trips, children: <JobTripsTab jobId={job.id} /> },
    { key: 'history', label: JOB_TAB_LABELS.history, children: <JobHistoryTab jobId={job.id} /> },
  ];
}
